import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";

// Lazy-load hCaptcha JS only when we actually need it
function loadScript() {
  if (document.getElementById("hcaptcha-js")) return Promise.resolve();
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.id = "hcaptcha-js";
    s.src = "https://hcaptcha.com/1/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = resolve;
    document.head.appendChild(s);
  });
}

/**
 * Inline hCaptcha widget. If captcha is disabled server-side, renders nothing
 * and calls onToken(null). Parent should pass null as a no-op token to backend.
 *
 * Props:
 *  - onToken(token | null)
 *  - theme: "dark" (default) | "light"
 */
export default function CaptchaWidget({ onToken, theme = "dark" }) {
  const [config, setConfig] = useState(null);
  const ref = useRef(null);
  const idRef = useRef(null);

  useEffect(() => {
    api.get("/captcha/config").then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
  }, []);

  useEffect(() => {
    if (!config) return;
    if (!config.enabled || !config.site_key) {
      onToken?.(null);
      return;
    }
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled) return;
      const attempt = () => {
        if (!window.hcaptcha || !ref.current) return setTimeout(attempt, 100);
        try {
          idRef.current = window.hcaptcha.render(ref.current, {
            sitekey: config.site_key,
            theme,
            callback: (token) => onToken?.(token),
            "expired-callback": () => onToken?.(null),
            "error-callback": () => onToken?.(null),
          });
        } catch (err) {
          // hcaptcha throws if the container is already rendered — safe to ignore,
          // but log in dev so a real misconfiguration doesn't go unnoticed.
          if (process.env.NODE_ENV !== "production") {
            console.debug("CaptchaWidget: render skipped (likely already mounted)", err);
          }
        }
      };
      attempt();
    });
    return () => {
      cancelled = true;
      try {
        if (idRef.current != null && window.hcaptcha) window.hcaptcha.reset(idRef.current);
      } catch (err) {
        // Reset can fail if the widget was already torn down — non-fatal.
        if (process.env.NODE_ENV !== "production") {
          console.debug("CaptchaWidget: reset failed", err);
        }
      }
    };
  }, [config, theme, onToken]);

  if (!config?.enabled) return null;
  return <div ref={ref} data-testid="hcaptcha-widget" className="my-3" />;
}
